import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import evidenceAPI from '../services/evidenceAPI';
import type {
  EvidenceSource,
  EvidenceItem,
  EvidenceFilters as Filters,
  EvidenceStats
} from '../types/evidence';
import EvidenceSourceGrid from '../components/Evidence/EvidenceSourceGrid';
import EvidenceList from '../components/Evidence/EvidenceList';
import EvidenceFilters from '../components/Evidence/EvidenceFilters';
import EvidenceDetailModal from '../components/Evidence/EvidenceDetailModal';
import AddSourceModal from '../components/Evidence/AddSourceModal';
import UploadEvidenceModal from '../components/Evidence/UploadEvidenceModal';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';
import CaptureScreenshotModal from '../components/Evidence/CaptureScreenshotModal';
import ScheduleAutoCaptureModal from '../components/Evidence/ScheduleAutoCaptureModal';
import {
  RefreshCw,
  PackageCheck,
  Upload,
  Download,
  BarChart3,
  AlertTriangle,
  Plus,
  Camera,
  Calendar,
  Trash2,
  X,
  Loader2,
  Bot,
  Send,
  Zap,
  FileQuestion,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
  Search,
  FileText,
  ArrowRight,
  Shield,
} from 'lucide-react';

// ─── AI ASSISTANT SUGGESTED QUERIES ─────────────────────────────────────────
const AI_SUGGESTED_QUERIES = [
  'What is our password policy?',
  'Do we have encryption in transit and at rest?',
  'Can contractors access production systems?',
  'What MFA methods do we support?',
  'What is our incident response SLA?',
];

const EvidenceHarvesting: React.FC = () => {
  // State
  const [sources, setSources] = useState<EvidenceSource[]>([]);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<EvidenceItem[]>([]);
  const [stats, setStats] = useState<EvidenceStats | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedEvidence, setSelectedEvidence] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editSource, setEditSource] = useState<EvidenceSource | null>(null);
  const [syncingSourceIds, setSyncingSourceIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [generatingPackages, setGeneratingPackages] = useState<Set<string>>(new Set());
  const [generatedPackages, setGeneratedPackages] = useState<Map<string, any>>(new Map());

  // AI Tools state
  const [aiQuery, setAiQuery] = useState('');
  type AiMessage = { role: 'user' | 'bot'; text: string; sources?: string[]; type?: string };
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([
    { role: 'bot', text: 'Hello! I can search your evidence, detect policy contradictions, and translate framework requirements. Ask me anything about your compliance posture.', type: 'info' }
  ]);
  const [aiLoading, setAiLoading] = useState(false);
  const aiChatEndRef = useRef<HTMLDivElement>(null);

  const [changeInput, setChangeInput] = useState('');
  type ChangeResult = { summary: { controls_affected: number; policies_to_update: number; evidence_needed: number; standards_improved: number }; required_actions: { title: string; update: string; tags: string[]; risk: string }[]; compliance_forecast: { standard: string; before: number; after: number; direction: string }[] };
  const [changeResult, setChangeResult] = useState<ChangeResult | null>(null);
  const [changeLoading, setChangeLoading] = useState(false);

  const [questionnaireText, setQuestionnaireText] = useState('');
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  type QAnswer = { id: number; question: string; answer: string; confidence: number; sources: string[]; level: 'HIGH' | 'MEDIUM' | 'LOW'; needs_review: boolean };
  type QResult = { answers: QAnswer[]; summary: { total: number; high_confidence: number; medium_confidence: number; needs_review: number; avg_confidence: number }; evidence_used: number };
  const [questionnaireResult, setQuestionnaireResult] = useState<QResult | null>(null);
  const [questionnaireProgress, setQuestionnaireProgress] = useState(0);
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);

  const [activeAITab, setActiveAITab] = useState<'assistant' | 'impact' | 'questionnaire'>('assistant');

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    sourceId: number | null;
    sourceName: string;
    isDeleting: boolean;
    error: string | null;
    evidenceCount: number | null;
    showForceOption: boolean;
  }>({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [evidenceItems, filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [sourcesData, itemsData, statsData] = await Promise.all([
        evidenceAPI.getEvidenceSources(),
        evidenceAPI.getEvidenceItems(),
        evidenceAPI.getEvidenceStats()
      ]);

      setSources(sourcesData);
      setEvidenceItems(itemsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load evidence data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...evidenceItems];

    if (filters.status) {
      filtered = filtered.filter(item => item.source?.status === filters.status);
    }

    if (filters.sourceType) {
      filtered = filtered.filter(item => item.source?.source_type === filters.sourceType);
    }

    if (filters.evidenceType) {
      filtered = filtered.filter(item => item.evidence_type === filters.evidenceType);
    }

    if (filters.validationStatus) {
      filtered = filtered.filter(item => item.validation_status === filters.validationStatus);
    }

    if (filters.isStale !== undefined) {
      filtered = filtered.filter(item => item.is_stale === filters.isStale);
    }

    setFilteredItems(filtered);
  };

  const handleSync = async (sourceId: number) => {
    setSyncingSourceIds(prev => new Set(prev).add(sourceId));

    const stopSyncing = () => {
      setSyncingSourceIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sourceId);
        return newSet;
      });
    };

    try {
      await evidenceAPI.syncSource(sourceId);

      // SSL Labs scans take 3-5 min; GitHub/AWS are fast (~30s)
      const sourceType = sources.find(s => s.id === sourceId)?.source_type ?? '';
      const isSlowScan = sourceType === 'ssl_labs' || sourceType === 'ssl_certificate';
      // slow: 100 polls × 5s = ~8 min | fast: 40 polls × 3s = 2 min
      const maxPolls = isSlowScan ? 100 : 40;
      const pollDelay = isSlowScan ? 5000 : 3000;
      let pollCount = 0;

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const updatedSource = await evidenceAPI.getEvidenceSourceById(sourceId);

          if (updatedSource.status !== 'syncing' || pollCount >= maxPolls) {
            clearInterval(pollInterval);
            stopSyncing();
            await loadData();
          }
        } catch (pollError) {
          clearInterval(pollInterval);
          stopSyncing();
        }
      }, pollDelay);
    } catch (error) {
      console.error('Sync failed:', error);
      stopSyncing();
    }
  };

  const handleSyncAll = async () => {
    setIsSyncingAll(true);

    try {
      const activeSources = sources.filter(s => s.status === 'active');
      const sourceIds = activeSources.map(s => s.id);
      setSyncingSourceIds(new Set(sourceIds));

      await evidenceAPI.syncAllSources();

      // Poll for status updates — max 40 polls × 3s = 2 minutes
      const maxPolls = 40;
      let pollCount = 0;

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const updatedSources = await evidenceAPI.getEvidenceSources();
          const stillSyncing = updatedSources.filter(s =>
            sourceIds.includes(s.id) && s.status === 'syncing'
          );

          if (stillSyncing.length === 0 || pollCount >= maxPolls) {
            clearInterval(pollInterval);
            await loadData();
            setSyncingSourceIds(new Set());
            setIsSyncingAll(false);
          }
        } catch (error) {
          clearInterval(pollInterval);
          setSyncingSourceIds(new Set());
          setIsSyncingAll(false);
        }
      }, 3000);
    } catch (error) {
      console.error('Sync all failed:', error);
      setSyncingSourceIds(new Set());
      setIsSyncingAll(false);
    }
  };

  const handleEvidenceClick = (item: EvidenceItem) => {
    setSelectedEvidence(item.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvidence(null);
  };

  const handleModalUpdate = () => {
    loadData();
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const handleEdit = (source: EvidenceSource) => {
    setEditSource(source);
    setIsAddSourceModalOpen(true);
  };

  const handleDeleteClick = (sourceId: number) => {
    const source = sources.find(s => s.id === sourceId);
    setDeleteModalState({
      isOpen: true,
      sourceId,
      sourceName: source?.source_name || 'this source',
      isDeleting: false,
      error: null,
      evidenceCount: null,
      showForceOption: false
    });
  };

  const handleDeleteConfirm = async (force: boolean = false) => {
    if (!deleteModalState.sourceId) return;

    setDeleteModalState(prev => ({ ...prev, isDeleting: true, error: null }));

    try {
      await evidenceAPI.deleteEvidenceSource(deleteModalState.sourceId, force);
      toast.success(force ? 'Evidence source and all items deleted successfully' : 'Evidence source deleted successfully');
      setDeleteModalState({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });
      await loadData();
    } catch (error: any) {
      console.error('Failed to delete source:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete evidence source';
      const evidenceCount = error.response?.data?.evidenceCount || null;

      // If there are evidence items, show force delete option
      if (evidenceCount && evidenceCount > 0) {
        setDeleteModalState(prev => ({
          ...prev,
          isDeleting: false,
          error: errorMessage,
          evidenceCount,
          showForceOption: true
        }));
      } else {
        setDeleteModalState(prev => ({ ...prev, isDeleting: false, error: errorMessage }));
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });
  };

  // ─── AI Tool Handlers ────────────────────────────────────────────────────

  const handleAIQuery = async (q?: string) => {
    const query = q || aiQuery;
    if (!query.trim()) return;
    const userMsg: AiMessage = { role: 'user', text: query };
    setAiMessages(prev => [...prev, userMsg]);
    setAiQuery('');
    setAiLoading(true);
    try {
      const result = await evidenceAPI.aiAssistantQuery(query);
      const botMsg: AiMessage = { role: 'bot', text: result.answer, sources: result.sources, type: result.type };
      setAiMessages(prev => [...prev, botMsg]);
      setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      setAiMessages(prev => [...prev, { role: 'bot', text: 'Sorry, AI assistant is unavailable. Please make sure the backend server is running.', type: 'warning' }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleChangeImpact = async () => {
    if (!changeInput.trim()) return;
    setChangeLoading(true);
    setChangeResult(null);
    try {
      const result = await evidenceAPI.aiChangeImpact(changeInput);
      setChangeResult(result);
    } catch {
      toast.error('Change impact analysis failed. Ensure backend is running.');
    } finally {
      setChangeLoading(false);
    }
  };

  const handleQuestionnaireAutofill = async () => {
    const questions = questionnaireText.trim().split('\n').map(q => q.trim()).filter(Boolean);
    if (questions.length === 0) { toast.error('Enter at least one question'); return; }
    setQuestionnaireLoading(true);
    setQuestionnaireResult(null);
    setQuestionnaireProgress(0);

    // Fake progress animation
    const prog = setInterval(() => setQuestionnaireProgress(p => Math.min(p + 12, 88)), 300);

    try {
      const result = await evidenceAPI.aiQuestionnaireAutofill(questions);
      clearInterval(prog);
      setQuestionnaireProgress(100);
      setQuestionnaireResult(result);
    } catch {
      clearInterval(prog);
      toast.error('Questionnaire auto-fill failed. Ensure backend is running.');
    } finally {
      setQuestionnaireLoading(false);
    }
  };

  const handleAddSourceSuccess = async (sourceId?: number, shouldSync?: boolean) => {
    await loadData();
    setIsAddSourceModalOpen(false);
    setEditSource(null);

    // Auto-sync/scan after saving
    if (shouldSync && sourceId) {
      setTimeout(() => {
        handleSync(sourceId);
      }, 500);
    }
  };

  const handleDownloadPackage = async (packageType: string) => {
    const pkg = generatedPackages.get(packageType);
    if (!pkg) return;
    try {
      await evidenceAPI.downloadPackage(pkg.id, pkg.package_name || `${packageType}-evidence`);
    } catch (err: any) {
      toast.error(`Download failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleGeneratePackage = async (packageType: string) => {
    if (generatingPackages.has(packageType)) return;

    // Label map for display
    const labelMap: Record<string, string> = {
      complete: 'Complete Audit Package',
      soc2: 'SOC 2 Evidence Package',
      iso27001: 'ISO 27001 Evidence Package',
      pci_dss: 'PCI DSS Evidence Package'
    };
    const label = labelMap[packageType] || packageType;

    // Framework tag filter map
    const tagMap: Record<string, string[]> = {
      soc2: ['SOC2', 'SOC_2', 'SOC2_CC'],
      iso27001: ['ISO27001', 'ISO_27001', 'ISO27001_A'],
      pci_dss: ['PCI_DSS', 'PCI', 'PCIDSS']
    };

    try {
      // Filter evidence by framework compliance tags
      let relevantEvidence = evidenceItems;
      if (packageType !== 'complete') {
        const tags = tagMap[packageType] || [packageType.toUpperCase()];
        relevantEvidence = evidenceItems.filter(item =>
          item.compliance_tags?.some(tag =>
            tags.some(t => tag.toUpperCase().includes(t))
          )
        );
        // If no tag matches, include all evidence (auditor gets everything)
        if (relevantEvidence.length === 0) {
          relevantEvidence = evidenceItems;
        }
      }

      if (relevantEvidence.length === 0) {
        toast.error('No evidence items found. Sync your evidence sources first.');
        return;
      }

      setGeneratingPackages(prev => new Set(prev).add(packageType));
      toast.loading(`Generating ${label}...`, { id: `pkg-${packageType}` });

      const packageData = {
        package_name: `${label} - ${new Date().toLocaleDateString()}`,
        package_description: `Auditor-ready evidence package for ${label} compliance framework`,
        package_type: packageType as any,
        evidence_item_ids: relevantEvidence.map(e => e.id),
        regulatory_frameworks: packageType === 'complete' ? ['SOC2', 'ISO27001', 'PCI_DSS'] : [packageType.toUpperCase()],
        compliance_tags: []
      };

      const pkg = await evidenceAPI.createEvidencePackage(packageData);

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const updatedPkg = await evidenceAPI.getEvidencePackageById(pkg.id);

          if (updatedPkg.generation_status === 'completed') {
            clearInterval(pollInterval);
            setGeneratingPackages(prev => { const s = new Set(prev); s.delete(packageType); return s; });
            setGeneratedPackages(prev => new Map(prev).set(packageType, updatedPkg));
            toast.success(
              `${label} ready! ${updatedPkg.total_evidence_count || 0} items · ${((updatedPkg.total_package_size_bytes || 0) / 1024).toFixed(0)} KB`,
              { id: `pkg-${packageType}`, duration: 6000 }
            );
          } else if (updatedPkg.generation_status === 'failed') {
            clearInterval(pollInterval);
            setGeneratingPackages(prev => { const s = new Set(prev); s.delete(packageType); return s; });
            toast.error(`Package generation failed. Please try again.`, { id: `pkg-${packageType}` });
          }
        } catch (pollError) {
          console.error('Error polling package status:', pollError);
        }
      }, 3000);

      // 5 min timeout
      setTimeout(() => {
        clearInterval(pollInterval);
        setGeneratingPackages(prev => { const s = new Set(prev); s.delete(packageType); return s; });
        toast.dismiss(`pkg-${packageType}`);
      }, 300000);

    } catch (error: any) {
      setGeneratingPackages(prev => { const s = new Set(prev); s.delete(packageType); return s; });
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to generate package: ${errorMessage}`, { id: `pkg-${packageType}` });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Evidence Harvesting</h1>
          <p className="text-gray-600">
            Automated evidence collection and management for compliance audits
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AdminSecurityHelpButton screenId="evidence-harvesting" />
          <button
            onClick={() => setIsAddSourceModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Source
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Sources</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_sources}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.active_sources} active
                </p>
              </div>
              <PackageCheck className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Evidence</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_evidence}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.validated_evidence} validated
                </p>
              </div>
              <BarChart3 className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Recent Collections</p>
                <p className="text-3xl font-bold text-gray-900">{stats.recent_collections}</p>
                <p className="text-xs text-gray-600 mt-1">Last 30 days</p>
              </div>
              <Download className="h-12 w-12 text-purple-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Stale Evidence</p>
                <p className="text-3xl font-bold text-gray-900">{stats.stale_evidence}</p>
                <p className="text-xs text-orange-600 mt-1">
                  {stats.stale_evidence > 0 ? 'Needs attention' : 'All current'}
                </p>
              </div>
              <AlertTriangle className="h-12 w-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>
      )}

      {/* Evidence Sources Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Evidence Sources</h2>
            <button
              onClick={handleSyncAll}
              disabled={isSyncingAll || sources.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncingAll ? 'animate-spin' : ''}`} />
              {isSyncingAll ? 'Syncing All...' : 'Sync All Sources'}
            </button>
          </div>

          <EvidenceSourceGrid
            sources={sources}
            onSync={handleSync}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            syncingSourceIds={syncingSourceIds}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Evidence Items Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Collected Evidence</h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {filteredItems.length} of {evidenceItems.length} items
              </span>
            </div>
          </div>

          <div className="mb-6">
            <EvidenceFilters
              filters={filters}
              onFilterChange={setFilters}
              onReset={handleResetFilters}
            />
          </div>

          <EvidenceList
            items={filteredItems}
            onItemClick={handleEvidenceClick}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Auditor Packages Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">⚡ Auditor Evidence Packages</h2>
          <p className="text-sm text-gray-500 mb-6">
            Generate pre-validated, auditor-ready evidence packages organized by framework
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Complete Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('complete') ?
                  handleDownloadPackage('complete') :
                  handleGeneratePackage('complete')
                }
                disabled={generatingPackages.has('complete')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('complete')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('complete')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('complete') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('complete') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('complete') ? 'Download Ready' : 'Complete Package'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('complete') ? 'Generating...' : 'All evidence'}
                </span>
              </button>
            </div>

            {/* SOC 2 Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('soc2') ?
                  handleDownloadPackage('soc2') :
                  handleGeneratePackage('soc2')
                }
                disabled={generatingPackages.has('soc2')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('soc2')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('soc2')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('soc2') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('soc2') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('soc2') ? 'Download Ready' : 'SOC 2 Package'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('soc2') ? 'Generating...' : 'Trust criteria'}
                </span>
              </button>
            </div>

            {/* ISO 27001 Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('iso27001') ?
                  handleDownloadPackage('iso27001') :
                  handleGeneratePackage('iso27001')
                }
                disabled={generatingPackages.has('iso27001')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('iso27001')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('iso27001')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('iso27001') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('iso27001') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('iso27001') ? 'Download Ready' : 'ISO 27001'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('iso27001') ? 'Generating...' : 'Information security'}
                </span>
              </button>
            </div>

            {/* PCI DSS Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('pci_dss') ?
                  handleDownloadPackage('pci_dss') :
                  handleGeneratePackage('pci_dss')
                }
                disabled={generatingPackages.has('pci_dss')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('pci_dss')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('pci_dss')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('pci_dss') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('pci_dss') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('pci_dss') ? 'Download Ready' : 'PCI DSS'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('pci_dss') ? 'Generating...' : 'Payment security'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Actions Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Evidence Actions</h2>
          <p className="text-sm text-gray-600 mb-6">
            Quick actions for manual evidence collection and management.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
            >
              <Upload className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Upload Manual Evidence</span>
              <span className="text-xs text-gray-500 mt-1">Audit reports, certifications</span>
            </button>

            <button
              onClick={() => setIsScreenshotModalOpen(true)}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
            >
              <Camera className="h-8 w-8 text-gray-400 group-hover:text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Capture Screenshot</span>
              <span className="text-xs text-gray-500 mt-1">Timestamped control evidence</span>
            </button>

            <button
              onClick={() => setIsScheduleModalOpen(true)}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group"
            >
              <Calendar className="h-8 w-8 text-gray-400 group-hover:text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Schedule Auto-Capture</span>
              <span className="text-xs text-gray-500 mt-1">Automated evidence collection</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── AI-Powered Tools have been moved to dedicated pages in Admin > Evidence Collection ─── */}
      {false && <div className="mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">AI-Powered Tools</h2>
                <p className="text-indigo-200 text-sm mt-0.5">Intelligent analysis powered by your real evidence data</p>
              </div>
              <div className="ml-auto flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-white text-xs font-medium">{evidenceItems.length} evidence items loaded</span>
              </div>
            </div>

            {/* Tab Bar */}
            <div className="flex gap-1 mt-4">
              {([
                { id: 'assistant', label: 'AI Assistant', icon: Bot },
                { id: 'impact', label: 'Change Impact', icon: Zap },
                { id: 'questionnaire', label: 'Questionnaire Auto-Fill', icon: FileQuestion },
              ] as const).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveAITab(id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeAITab === id
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ── TAB 1: AI ASSISTANT ─────────────────────────────────────────── */}
          {activeAITab === 'assistant' && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Chat Area */}
                <div className="lg:col-span-2 flex flex-col">
                  <div className="flex-1 bg-gray-50 rounded-xl border border-gray-200 p-4 h-80 overflow-y-auto space-y-3">
                    {aiMessages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'bot' && (
                          <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                            <Bot className="w-4 h-4 text-indigo-600" />
                          </div>
                        )}
                        <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                          <div className={`rounded-xl px-4 py-2.5 text-sm ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white'
                              : msg.type === 'warning'
                              ? 'bg-amber-50 border border-amber-200 text-amber-900'
                              : msg.type === 'success'
                              ? 'bg-green-50 border border-green-200 text-green-900'
                              : 'bg-white border border-gray-200 text-gray-800'
                          }`}>
                            {msg.text}
                          </div>
                          {msg.sources && msg.sources.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {msg.sources.map((s, si) => (
                                <span key={si} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded px-2 py-0.5">
                                  <FileText className="w-3 h-3" />
                                  {s}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {aiLoading && (
                      <div className="flex justify-start">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0">
                          <Bot className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={aiChatEndRef} />
                  </div>

                  {/* Input */}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={aiQuery}
                      onChange={e => setAiQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !aiLoading && handleAIQuery()}
                      placeholder="Ask about your compliance posture, policies, or evidence gaps..."
                      className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={() => handleAIQuery()}
                      disabled={aiLoading || !aiQuery.trim()}
                      className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Suggested Queries + Framework Translation */}
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Suggested Queries</p>
                    <div className="space-y-2">
                      {AI_SUGGESTED_QUERIES.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => handleAIQuery(q)}
                          disabled={aiLoading}
                          className="w-full text-left text-sm px-3 py-2.5 rounded-lg border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 text-gray-700 transition-all flex items-center justify-between group"
                        >
                          <span>{q}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-2" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                    <p className="text-xs font-semibold text-indigo-700 mb-1.5 flex items-center gap-1">
                      <Search className="w-3.5 h-3.5" /> Searching across
                    </p>
                    <p className="text-sm text-indigo-800 font-medium">{evidenceItems.length} evidence items</p>
                    <p className="text-xs text-indigo-600 mt-0.5">
                      {sources.length} active sources
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── TAB 2: CHANGE IMPACT ────────────────────────────────────────── */}
          {activeAITab === 'impact' && (
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Describe your planned change</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={changeInput}
                    onChange={e => setChangeInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !changeLoading && handleChangeImpact()}
                    placeholder="e.g. Adding a public API endpoint, enabling MFA, changing firewall rules..."
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleChangeImpact}
                    disabled={changeLoading || !changeInput.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {changeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    Analyze
                  </button>
                </div>
                {/* Quick examples */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Adding public API endpoint', 'Enable MFA for all users', 'Change firewall rules', 'Upgrade TLS certificates'].map(ex => (
                    <button key={ex} onClick={() => setChangeInput(ex)} className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 rounded-full border border-gray-200 transition-colors">
                      {ex}
                    </button>
                  ))}
                </div>
              </div>

              {changeLoading && (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-3 text-indigo-500" />
                  <span className="text-sm">Analyzing compliance impact against {evidenceItems.length} evidence items...</span>
                </div>
              )}

              {changeResult && (
                <div className="space-y-5">
                  {/* Summary Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { label: 'Controls Affected', value: changeResult.summary.controls_affected, color: 'amber', icon: Shield },
                      { label: 'Policies to Update', value: changeResult.summary.policies_to_update, color: 'blue', icon: FileText },
                      { label: 'Evidence Needed', value: changeResult.summary.evidence_needed, color: 'purple', icon: PackageCheck },
                      { label: 'Standards Improved', value: changeResult.summary.standards_improved, color: 'green', icon: TrendingUp },
                    ].map(({ label, value, color }) => (
                      <div key={label} className={`rounded-xl p-4 border bg-${color}-50 border-${color}-100`}>
                        <p className={`text-xs text-${color}-600 font-medium mb-1`}>{label}</p>
                        <p className={`text-3xl font-bold text-${color}-700`}>{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Required Actions */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Required Actions</p>
                      <div className="space-y-2">
                        {changeResult.required_actions.map((action, i) => (
                          <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className={`w-2 flex-shrink-0 rounded-full mt-1 self-start h-2 ${action.risk === 'HIGH' ? 'bg-red-500' : action.risk === 'MEDIUM' ? 'bg-amber-500' : 'bg-green-500'}`} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800">{action.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Update: {action.update}</p>
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {action.tags.map(t => (
                                  <span key={t} className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">{t}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Compliance Forecast */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Compliance Score Forecast</p>
                      <div className="space-y-3">
                        {changeResult.compliance_forecast.map((f, i) => (
                          <div key={i}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-700">{f.standard}</span>
                              <span className={`text-sm font-semibold ${f.direction === 'up' ? 'text-green-600' : f.direction === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
                                {f.before}% → {f.after}%
                                {f.direction === 'up' && <TrendingUp className="inline w-3.5 h-3.5 ml-1" />}
                                {f.direction === 'down' && <AlertCircle className="inline w-3.5 h-3.5 ml-1" />}
                              </span>
                            </div>
                            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="absolute inset-y-0 left-0 bg-gray-300 rounded-full" style={{ width: `${f.before}%` }} />
                              <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${f.direction === 'up' ? 'bg-green-500' : f.direction === 'down' ? 'bg-red-400' : 'bg-blue-400'}`} style={{ width: `${f.after}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!changeResult && !changeLoading && (
                <div className="text-center py-10 text-gray-400">
                  <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Describe a planned change to see its compliance impact</p>
                  <p className="text-xs mt-1">Analyzes impact using your current {evidenceItems.length} evidence items</p>
                </div>
              )}
            </div>
          )}

          {/* ── TAB 3: QUESTIONNAIRE AUTO-FILL ──────────────────────────────── */}
          {activeAITab === 'questionnaire' && (
            <div className="p-6">
              {!questionnaireResult ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Paste security questionnaire questions <span className="text-gray-400 font-normal">(one per line)</span>
                    </label>
                    <textarea
                      value={questionnaireText}
                      onChange={e => setQuestionnaireText(e.target.value)}
                      rows={10}
                      placeholder={"Do you enforce MFA for all accounts?\nHow is data encrypted at rest and in transit?\nWhat is your vulnerability patching cadence?\nDo you conduct security awareness training?\nWhat is your incident response SLA?"}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono"
                    />
                    <button
                      onClick={handleQuestionnaireAutofill}
                      disabled={questionnaireLoading || !questionnaireText.trim()}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {questionnaireLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Auto-filling ({questionnaireProgress}%)...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Auto-Fill with AI
                        </>
                      )}
                    </button>
                    {questionnaireLoading && (
                      <div className="mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${questionnaireProgress}%` }} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">How it works</p>
                    {[
                      { icon: FileText, text: 'Paste questions from any security questionnaire (ISO, SOC 2, vendor forms)' },
                      { icon: Search, text: `AI searches across your ${evidenceItems.length} collected evidence items` },
                      { icon: CheckCircle, text: 'Each answer gets a confidence score based on available evidence' },
                      { icon: AlertCircle, text: 'Low-confidence answers are flagged for human review' },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <Icon className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-600">{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  {/* Summary bar */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-sm text-gray-600">{questionnaireResult.summary.high_confidence} High confidence</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-sm text-gray-600">{questionnaireResult.summary.medium_confidence} Medium</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-400" />
                        <span className="text-sm text-gray-600">{questionnaireResult.summary.needs_review} Needs review</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">Avg confidence: <span className="font-semibold text-indigo-600">{questionnaireResult.summary.avg_confidence}%</span></span>
                      <button
                        onClick={() => setQuestionnaireResult(null)}
                        className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        New Questionnaire
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                    {questionnaireResult.answers.map((ans) => (
                      <div key={ans.id} className={`border rounded-xl overflow-hidden transition-all ${
                        ans.level === 'HIGH' ? 'border-green-200' : ans.level === 'MEDIUM' ? 'border-amber-200' : 'border-red-200'
                      }`}>
                        <button
                          className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedQuestion(expandedQuestion === ans.id ? null : ans.id)}
                        >
                          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center text-xs font-bold ${
                            ans.level === 'HIGH' ? 'bg-green-100 text-green-700' :
                            ans.level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            <span className="text-base font-bold">{ans.confidence}</span>
                            <span className="text-[10px] leading-none">%</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                ans.level === 'HIGH' ? 'bg-green-100 text-green-700' :
                                ans.level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>{ans.level}</span>
                              {ans.needs_review && <span className="text-xs text-red-600 font-medium">⚠ Needs Review</span>}
                            </div>
                            <p className="text-sm font-medium text-gray-800 mt-1">{ans.question}</p>
                            {expandedQuestion !== ans.id && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{ans.answer}</p>
                            )}
                          </div>
                          {expandedQuestion === ans.id ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
                        </button>

                        {expandedQuestion === ans.id && (
                          <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gray-50">
                            <p className="text-sm text-gray-800">{ans.answer}</p>
                            {ans.sources.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {ans.sources.map((s, si) => (
                                  <span key={si} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* Evidence Detail Modal */}
      <EvidenceDetailModal
        evidenceId={selectedEvidence}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpdate={handleModalUpdate}
      />

      {/* Add/Edit Source Modal */}
      <AddSourceModal
        isOpen={isAddSourceModalOpen}
        onClose={() => {
          setIsAddSourceModalOpen(false);
          setEditSource(null);
        }}
        onSuccess={handleAddSourceSuccess}
        editSource={editSource || undefined}
      />

      {/* Upload Evidence Modal */}
      <UploadEvidenceModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsUploadModalOpen(false);
        }}
      />

      {/* Capture Screenshot Modal */}
      <CaptureScreenshotModal
        isOpen={isScreenshotModalOpen}
        onClose={() => setIsScreenshotModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsScreenshotModalOpen(false);
        }}
      />

      {/* Schedule Auto-Capture Modal */}
      <ScheduleAutoCaptureModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsScheduleModalOpen(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleDeleteCancel} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 transform transition-all">
              <button
                onClick={handleDeleteCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Evidence Source</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">"{deleteModalState.sourceName}"</span>?
              </p>

              {deleteModalState.error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Warning</p>
                      <p className="text-sm text-amber-700 mt-1">{deleteModalState.error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 mt-6">
                {deleteModalState.showForceOption ? (
                  <>
                    <button
                      onClick={() => handleDeleteConfirm(true)}
                      disabled={deleteModalState.isDeleting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteModalState.isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete Source & {deleteModalState.evidenceCount} Evidence Items
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDeleteCancel}
                      disabled={deleteModalState.isDeleting}
                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteCancel}
                      disabled={deleteModalState.isDeleting}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteConfirm(false)}
                      disabled={deleteModalState.isDeleting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteModalState.isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete Source
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceHarvesting;
